import {
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  Store as StoreIcon,
  Storage as DatabaseIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Layers as LayersIcon,
  Person as UserIcon,
  Login as EnterIcon,
  ExitToApp as ExitIcon,
  AccessTime as ClockIcon,
  CancelOutlined as CancelIcon,
  Map as MapIcon,
  CleaningServices as CleaningServicesIcon,
  AttachMoney as RateIcon,
  RestaurantMenu as RestaurantMenuIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  TaskAlt as TaskAltIcon,
  MapsHomeWork as HallIcon,
} from '@mui/icons-material';
import {
  getUserPermissionCodes,
  hasFeaturePermission,
  isHotelFeatureEnabled,
} from '../utils/featurePermissions';

export const mobileNavIconMap = {
  'lnr-home': HomeIcon,
  'lnr-calendar-full': CalendarIcon,
  'lnr-store': StoreIcon,
  'lnr-database': DatabaseIcon,
  'lnr-users': PeopleIcon,
  'lnr-cog': SettingsIcon,
  'lnr-layers': LayersIcon,
  'lnr-user': UserIcon,
  'lnr-enter': EnterIcon,
  'lnr-exit': ExitIcon,
  'lnr-clock': ClockIcon,
  'lnr-cross-circle': CancelIcon,
  'lnr-map': MapIcon,
  'lnr-apartment': HallIcon,
  'lnr-cleaning': CleaningServicesIcon,
  'lnr-tag': LocalOfferIcon,
  'lnr-rate': RateIcon,
  'mui-trending-up': TrendingUpIcon,
  'mui-assessment': AssessmentIcon,
};

const withAccessFlags = (item, { hotelEnabled, userEnabled }) => ({
  ...item,
  hotelEnabled,
  disabled: Boolean(hotelEnabled && !userEnabled),
});

export function getMobileNavGroups() {
  const userPermissions = getUserPermissionCodes();
  const can = (code) => hasFeaturePermission(code, userPermissions);
  const hotelHas = (code) => isHotelFeatureEnabled(code);

  const insights = [
    {
      title: 'Performance',
      shortTitle: 'Performance',
      icon: 'mui-trending-up',
      url: '/dashboard/performance/',
      permissionCode: 'common.view_organization_dashboard',
      hotelEnabled:
        hotelHas('common.view_organization_dashboard')
        || hotelHas('common.view_reports'),
      userEnabled:
        can('common.view_organization_dashboard')
        || can('common.view_reports'),
    },
    {
      title: 'Booking Dashboard',
      shortTitle: 'Bookings',
      icon: 'lnr-calendar-full',
      url: '/booking/dashboard/',
      permissionCode: 'common.view_organization_dashboard',
      hotelEnabled:
        hotelHas('common.view_organization_dashboard')
        || hotelHas('common.view_reports'),
      userEnabled:
        can('common.view_organization_dashboard')
        || can('common.view_reports'),
    },
    {
      title: 'Finance Dashboard',
      shortTitle: 'Finance',
      icon: 'lnr-store',
      url: '/finance/',
      permissionCode: 'common.manage_finance',
      hotelEnabled: hotelHas('common.manage_finance'),
      userEnabled: can('common.manage_finance'),
    },
    {
      title: 'Inventory Dashboard',
      shortTitle: 'Inventory',
      icon: 'lnr-database',
      url: '/inventory/',
      permissionCode: 'common.manage_inventory',
      hotelEnabled: hotelHas('common.manage_inventory'),
      userEnabled: can('common.manage_inventory'),
    },
    {
      title: 'Customer Dashboard',
      shortTitle: 'Customers',
      icon: 'lnr-users',
      url: '/customerhub/',
      permissionCode: 'common.manage_customer_management',
      hotelEnabled: hotelHas('common.manage_customer_management'),
      userEnabled: can('common.manage_customer_management'),
    },
    {
      title: 'Report',
      shortTitle: 'Report',
      icon: 'mui-assessment',
      url: '/finance/reports/',
      permissionCode: 'common.view_reports',
      hotelEnabled: hotelHas('common.view_reports') || hotelHas('common.manage_finance'),
      userEnabled: can('common.view_reports') || can('common.manage_finance'),
    },
  ]
    .filter((item) => item.hotelEnabled)
    .map((item) => withAccessFlags(item, item));

  const overview = [
    {
      title: 'Dashboard',
      shortTitle: 'Dashboard',
      icon: 'lnr-home',
      url: '/dashboard/',
      permissionCode: 'common.view_organization_dashboard',
      hotelEnabled:
        hotelHas('common.view_organization_dashboard')
        || hotelHas('common.manage_booking_operations'),
      userEnabled:
        can('common.view_organization_dashboard')
        || can('common.manage_booking_operations'),
    },
    {
      title: 'Booking',
      shortTitle: 'Booking',
      icon: 'lnr-calendar-full',
      url: '/booking/',
      permissionCode: 'common.manage_booking_operations',
      hotelEnabled: hotelHas('common.manage_booking_operations'),
      userEnabled: can('common.manage_booking_operations'),
    },
  ]
    .filter((item) => item.hotelEnabled)
    .map((item) => withAccessFlags(item, item));

  const bookings = [
    { title: 'Reservation', shortTitle: 'Reservation', icon: 'lnr-calendar-full', url: '/booking/reservation/', permissionCode: 'common.manage_booking_operations' },
    { title: 'Walk In', shortTitle: 'Walk In', icon: 'lnr-user', url: '/booking/walk-in/', permissionCode: 'common.manage_booking_operations' },
    { title: 'Stay', shortTitle: 'Stay', icon: 'lnr-clock', url: '/booking/stay/', permissionCode: 'common.manage_booking_operations' },
    { title: 'Check In', shortTitle: 'Check In', icon: 'lnr-enter', url: '/booking/checkin/', permissionCode: 'common.manage_booking_operations' },
    { title: 'Check Out', shortTitle: 'Check Out', icon: 'lnr-exit', url: '/booking/checkout/', permissionCode: 'common.manage_booking_operations' },
    { title: 'Room Allocation', shortTitle: 'Allocation', icon: 'lnr-map', url: '/booking/room-allocation/', permissionCode: 'common.manage_booking_operations' },
    { title: 'No Show', shortTitle: 'No Show', icon: 'lnr-cross-circle', url: '/booking/no-show/', permissionCode: 'common.manage_booking_operations' },
  ]
    .filter(() => hotelHas('common.manage_booking_operations'))
    .map((item) => withAccessFlags(item, {
      hotelEnabled: true,
      userEnabled: can('common.manage_booking_operations'),
    }));

  const apps = [
    {
      title: 'Manage Inventory',
      shortTitle: 'Inventory',
      icon: 'lnr-database',
      url: '/inventory/manage/',
      permissionCode: 'common.manage_inventory',
      hotelEnabled: hotelHas('common.manage_inventory'),
      userEnabled: can('common.manage_inventory'),
    },
    {
      title: 'Manage Rate',
      shortTitle: 'Rates',
      icon: 'lnr-rate',
      url: '/finance/rates/',
      permissionCode: 'common.manage_finance',
      hotelEnabled: hotelHas('common.manage_finance'),
      userEnabled: can('common.manage_finance'),
    },
    {
      title: 'Dynamic Pricing',
      shortTitle: 'Pricing',
      icon: 'lnr-tag',
      url: '/dynamic-pricing/',
      permissionCode: 'common.manage_dynamic_pricing',
      hotelEnabled: hotelHas('common.manage_dynamic_pricing'),
      userEnabled: can('common.manage_dynamic_pricing'),
    },
    {
      title: 'Distribution',
      shortTitle: 'Distribution',
      icon: 'lnr-layers',
      url: '/distribution/',
      permissionCode: 'common.manage_distribution',
      hotelEnabled: hotelHas('common.manage_distribution') || hotelHas('common.manage_booking_engine'),
      userEnabled: can('common.manage_distribution') || can('common.manage_booking_engine'),
    },
    {
      title: 'Configuration',
      shortTitle: 'Config',
      icon: 'lnr-cog',
      url: '/configuration/',
      permissionCode: 'common.manage_configuration',
      hotelEnabled: hotelHas('common.manage_configuration'),
      userEnabled: can('common.manage_configuration'),
    },
    {
      title: 'POS',
      shortTitle: 'POS',
      icon: 'lnr-store',
      url: '/pos-restaurant/',
      permissionCode: 'common.manage_pos_restaurant',
      hotelEnabled: hotelHas('common.manage_pos_restaurant'),
      userEnabled: can('common.manage_pos_restaurant'),
    },
  ]
    .filter((item) => item.hotelEnabled)
    .map((item) => withAccessFlags(item, item));

  const operations = [
    hotelHas('common.manage_pos_restaurant') && withAccessFlags({
      title: 'Restaurant',
      shortTitle: 'Restaurant',
      icon: 'lnr-store',
      url: '/restaurant/',
      permissionCode: 'common.manage_pos_restaurant',
    }, {
      hotelEnabled: true,
      userEnabled: can('common.manage_pos_restaurant'),
    }),
    hotelHas('common.manage_housekeeping') && withAccessFlags({
      title: 'Housekeeping',
      shortTitle: 'Housekeeping',
      icon: 'lnr-cleaning',
      url: '/housekeeping/',
      permissionCode: 'common.manage_housekeeping',
    }, {
      hotelEnabled: true,
      userEnabled: can('common.manage_housekeeping'),
    }),
    hotelHas('common.manage_stock') && withAccessFlags({
      title: 'Stock',
      shortTitle: 'Stock',
      icon: 'lnr-database',
      url: '/stock/',
      permissionCode: 'common.manage_stock',
    }, {
      hotelEnabled: true,
      userEnabled: can('common.manage_stock'),
    }),
    hotelHas('common.manage_banquet_conference') && withAccessFlags({
      title: 'Hall',
      shortTitle: 'Hall',
      icon: 'lnr-apartment',
      url: '/hall/dashboard/',
      permissionCode: 'common.manage_banquet_conference',
    }, {
      hotelEnabled: true,
      userEnabled: can('common.manage_banquet_conference'),
    }),
  ].filter(Boolean);

  // Home Quick Actions: max 7 tiles (+ More = 8). Allocation is available via More.
  const quickActions = [
    can('common.manage_booking_operations') && {
      title: 'Reservation',
      icon: CalendarIcon,
      url: '/booking/reservation/',
      color: '#38bdf8',
    },
    can('common.manage_booking_operations') && {
      title: 'Checkin',
      icon: EnterIcon,
      url: '/booking/checkin/',
      color: '#60a5fa',
    },
    can('common.manage_booking_operations') && {
      title: 'Checkout',
      icon: ExitIcon,
      url: '/booking/checkout/',
      color: '#f97316',
    },
    can('common.manage_banquet_conference') && {
      title: 'Hall',
      icon: HallIcon,
      url: '/hall/dashboard/',
      color: '#4ce1b6',
    },
    (can('common.view_organization_dashboard') || can('common.view_reports')) && {
      title: 'Performance',
      icon: TrendingUpIcon,
      url: '/dashboard/performance/',
      color: '#a78bfa',
    },
    can('common.manage_pos_restaurant') && {
      title: 'Restaurant',
      icon: RestaurantMenuIcon,
      url: '/restaurant/',
      color: '#fb7185',
    },
    can('common.manage_stock') && {
      title: 'Stock',
      icon: DatabaseIcon,
      url: '/stock/',
      color: '#fbbf24',
    },
  ].filter(Boolean).slice(0, 7);

  return {
    overview,
    bookings,
    insights,
    apps,
    operations,
    quickActions,
  };
}

export function getMobileBottomTabs(navGroups) {
  const userPermissions = getUserPermissionCodes();
  const can = (code) => hasFeaturePermission(code, userPermissions);

  return [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: HomeIcon,
      url: '/dashboard/',
      match: (path) => {
        if (path.includes('view=tasks')) return false;
        return path === '/dashboard/' || path === '/dashboard';
      },
    },
    can('common.manage_booking_operations') && {
      id: 'bookings',
      label: 'Bookings',
      icon: CalendarIcon,
      url: '/booking/',
      match: (path) => path.startsWith('/booking/'),
    },
    can('common.manage_booking_operations') && {
      id: 'tasks',
      label: 'Tasks',
      icon: TaskAltIcon,
      url: '/dashboard/?view=tasks',
      match: (path) => path.startsWith('/dashboard') && path.includes('view=tasks'),
    },
  ].filter(Boolean);
}

export function getMobileFabAction(navGroups) {
  const book = navGroups.bookings.find((item) => item.url === '/booking/walk-in/' && !item.disabled);
  return book || navGroups.bookings.find((item) => !item.disabled) || navGroups.bookings[0] || { url: '/booking/reservation/', title: 'Quick Book' };
}
